import heic2any from "heic2any";
import axiosInstance, { setAxiosToken } from "../helpers/axiosHelper";

/**
 * The one image-upload pipeline shared by every place a user picks a picture (profile photo,
 * studio logo). Callers keep their own error handling: they know which toast to show.
 */

/**
 * Photos taken on an iPhone arrive as HEIC, which no browser renders. Safari often hands
 * them over with an empty MIME type, so the extension is checked as well.
 */
export const isHeicImage = (file: File): boolean =>
  file.type === "image/heic" ||
  file.type === "image/heif" ||
  file.name.toLowerCase().endsWith(".heic") ||
  file.name.toLowerCase().endsWith(".heif");

export interface IPreparedImage {
  blob: File | Blob;
  fileName: string;
}

/** Converts HEIC to JPEG and renames accordingly; anything else passes through untouched. */
export const prepareImageForUpload = async (
  file: File,
): Promise<IPreparedImage> => {
  if (!isHeicImage(file)) {
    return { blob: file, fileName: file.name };
  }

  const converted = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.9,
  });

  return {
    // heic2any returns an array for multi-image containers; the first frame is the photo.
    blob: Array.isArray(converted) ? converted[0] : converted,
    fileName: file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg"),
  };
};

/**
 * Prepares the image and POSTs it as multipart form data to `endpoint`, returning the parsed
 * response body. Errors propagate so the caller can decide what to tell the user.
 */
export const uploadImageFile = async <TResponse = unknown>(
  endpoint: string,
  file: File,
  authToken?: string,
): Promise<TResponse> => {
  const { blob, fileName } = await prepareImageForUpload(file);

  setAxiosToken(authToken);

  const formData = new FormData();
  formData.append("file", blob, fileName);

  const response = await axiosInstance.post<TResponse>(endpoint, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
};
